import { Link } from 'react-router-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { ProductImage } from './ProductImage';
import { ProductPrice } from './ProductPrice';
import { CartQuantitySelector } from './cart/CartQuantitySelector';
import { CartEmptyState } from './cart/CartEmptyState';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  // Get storeSlug from CartContext (works everywhere, not just in /shop routes)
  const { cart, loading, isEmpty, updateQuantity, removeItem, goToCheckout, isUpdating, isCheckingOut, lastAddedVariantId, storeSlug } = useCart();

  const handleCheckout = async () => {
    try {
      await goToCheckout();
    } catch (error) {
      console.error('Checkout failed:', error);
    }
  };

  const handleQuantityChange = async (lineId: string, currentQty: number, delta: number) => {
    const newQty = currentQty + delta;
    if (newQty >= 0) {
      await updateQuantity(lineId, newQty);
    }
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop with fade animation */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              {/* Panel with slide animation */}
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-screen max-w-md">
              <div className="flex h-full flex-col overflow-y-auto bg-white dark:bg-secondary-900 shadow-xl">
                {/* Header */}
                <div className="flex items-start justify-between px-4 py-6 sm:px-6 border-b border-gray-200 dark:border-secondary-700">
                  <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                    Carrito ({cart?.totalQuantity || 0})
                  </DialogTitle>
                  <div className="ml-3 flex h-7 items-center">
                    <button
                      type="button"
                      onClick={onClose}
                      className="relative -m-2 p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                    >
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Cerrar carrito</span>
                      <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                  {isEmpty ? (
                    <CartEmptyState variant="drawer" />
                  ) : (
                    <ul role="list" className="-my-6 divide-y divide-gray-200 dark:divide-secondary-700">
                      {cart?.lines.map((line) => {
                        // Handle both plain ID and GID format matching
                        // lastAddedVariantId could be "44479448743981" 
                        // line.variantId could be "gid://shopify/ProductVariant/44479448743981"
                        const isNewlyAdded = lastAddedVariantId !== null && (
                          lastAddedVariantId === line.variantId ||
                          line.variantId.includes(lastAddedVariantId) ||
                          lastAddedVariantId.includes(line.variantId)
                        );
                        return (
                        <li 
                          key={line.id} 
                          className={`flex py-6 transition-all duration-500 ${
                            isNewlyAdded 
                              ? 'bg-green-50 -mx-4 px-4 rounded-lg ring-2 ring-green-400 ring-opacity-50 animate-pulse-once' 
                              : ''
                          }`}
                        >
                          {/* Image */}
                          <div className={`h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border ${
                            isNewlyAdded ? 'border-green-400 shadow-lg shadow-green-100' : 'border-gray-200'
                          }`}>
                            <ProductImage
                              src={line.image?.url}
                              alt={line.productTitle}
                              className="h-full w-full object-cover"
                            />
                          </div>

                          {/* Info */}
                          <div className="ml-4 flex flex-1 flex-col">
                            <div>
                              <div className="flex justify-between text-base font-medium text-gray-900 dark:text-white">
                                <h3>
                                  <Link
                                    to={`/shop/${storeSlug}/products/${line.productHandle}`}
                                    onClick={onClose}
                                    className="hover:text-primary-600 dark:hover:text-primary-400"
                                  >
                                    {line.productTitle}
                                  </Link>
                                </h3>
                                <ProductPrice
                                  amount={String(parseFloat(line.unitPrice.amount) * line.quantity)}
                                  currencyCode={line.unitPrice.currencyCode}
                                  className="ml-4"
                                />
                              </div>
                              {line.variantTitle && (
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                  {line.variantTitle}
                                </p>
                              )}
                            </div>

                            {/* Quantity controls and Remove */}
                            <div className="mt-auto pt-3 flex items-center justify-between">
                              <CartQuantitySelector
                                quantity={line.quantity}
                                onIncrease={() => handleQuantityChange(line.id, line.quantity, 1)}
                                onDecrease={() => handleQuantityChange(line.id, line.quantity, -1)}
                                onRemove={() => removeItem(line.id)}
                                isLoading={isUpdating}
                                max={line.quantityAvailable || undefined}
                                size="sm"
                              />

                              <button
                                type="button"
                                onClick={() => removeItem(line.id)}
                                disabled={isUpdating}
                                className="text-sm font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400 disabled:cursor-not-allowed"
                              >
                                Eliminar
                              </button>
                            </div>
                          </div>
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {/* Footer */}
                {!isEmpty && (
                  <div className="border-t border-gray-200 dark:border-secondary-700 px-4 py-6 sm:px-6">
                    {/* Totals */}
                    <div className="flex justify-between text-base font-medium text-gray-900 dark:text-white mb-4">
                      <p>Total</p>
                      <ProductPrice
                        amount={cart?.total.amount || '0'}
                        currencyCode={cart?.total.currencyCode || 'ARS'}
                      />
                    </div>

                    <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                      Impuestos y envío calculados en el checkout.
                    </p>

                    {/* Actions */}
                    <div className="space-y-3">
                      <button
                        onClick={handleCheckout}
                        disabled={loading || isEmpty || isCheckingOut}
                        className="flex w-full items-center justify-center rounded-md bg-primary-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-secondary-700 disabled:cursor-not-allowed transition-colors"
                      >
                        {isCheckingOut ? 'Procesando...' : 'Ir a pagar'}
                      </button>

                      <Link
                        to={`/shop/${storeSlug}/cart`}
                        onClick={onClose}
                        className="flex w-full items-center justify-center rounded-md border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 px-6 py-3 text-base font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                      >
                        Ver carrito completo
                      </Link>
                    </div>

                    <div className="mt-6 flex justify-center text-center text-sm text-gray-500 dark:text-gray-400">
                      <button
                        type="button"
                        onClick={onClose}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300"
                      >
                        Continuar comprando
                        <span aria-hidden="true"> &rarr;</span>
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>

      {/* Animation styles for newly added items */}
      <style>{`
        @keyframes pulse-once {
          0% {
            box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
          }
          50% {
            box-shadow: 0 0 0 10px rgba(74, 222, 128, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(74, 222, 128, 0);
          }
        }
        .animate-pulse-once {
          animation: pulse-once 1.5s ease-out;
        }
      `}</style>
    </Dialog>
  </Transition>
  );
}
